import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Alert, Button, Form, FormGroup, Input, Label } from 'reactstrap';
import { useGetEventAccountQuery } from '../../../graphql/queries/getEventAccount.generated';
import { useUpdateEventAccountMutation } from '../../../graphql/mutations/updateEventAccount.generated';
import { LayoutStoreInstance } from '../../../LayoutStore';
import {
  CheckboxLabel,
  VytalCheckboxLabelContainer,
} from '../../../pages/AdminStoreDetail/Menu/ProductItem/ProductOrderSheet/ExtraSelector/ExtraGroupEditModal/styled';
import Checkbox from '../../../components/Checkbox';
import { FaArrowLeft } from 'react-icons/fa';
import i18n from '@/utils/i18n';

export const EventAccountEditPage = () => {
  const { eventAccountId } = useParams<{ eventAccountId: string }>();

  const {
    data: eventAccountData,
    loading: queryLoading,
    error: errorLoadingEventAcc,
  } = useGetEventAccountQuery({ variables: { id: eventAccountId } });
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [sendOrderFulfillmentEmails, setSendOrderFulfillmentEmails] =
    useState(false);
  const [isBillable, setIsBillable] = useState(false);

  const history = useHistory();

  const [
    updateEventAccount,
    { loading: updateLoading, error: errorUpdatingEventAcc },
  ] = useUpdateEventAccountMutation();

  useEffect(() => {
    if (eventAccountData?.eventAccount) {
      setName(eventAccountData.eventAccount.name || '');
      setDescription(eventAccountData.eventAccount.description || '');
      setEmail(eventAccountData.eventAccount.email || '');
      setIsBillable(eventAccountData.eventAccount.is_billable || false);
      setSendOrderFulfillmentEmails(
        eventAccountData.eventAccount.send_order_fulfillment_emails || false,
      );
    }
  }, [eventAccountData]);

  const handleUpdateEventAccount = async () => {
    if (!name) {
      LayoutStoreInstance.setError(i18n.t('EventAccountEditPage:nameRequired'));
      return;
    }
    const result = await updateEventAccount({
      variables: {
        event_account_id: eventAccountId,
        input: {
          name,
          description,
          email,
          is_billable: isBillable,
          send_order_fulfillment_emails: sendOrderFulfillmentEmails,
        },
      },
    });
    if (result.errors) {
      LayoutStoreInstance.setError(
        i18n.t('EventAccountEditPage:errorUpdatingEventAccount'),
      );
    } else {
      LayoutStoreInstance.setSuccess(
        i18n.t('EventAccountEditPage:eventAccountUpdated'),
      );
    }
  };

  if (errorLoadingEventAcc)
    return <p>{i18n.t('EventAccountEditPage:errorLoadingEventAccount')}</p>;

  if (queryLoading) return <p>{i18n.t('Common:loading')}</p>;

  return (
    <div>
      <h2>{i18n.t('EventAccountEditPage:editEventAccount')}</h2>
      <Button className="float-end mt-2 mb-2" onClick={() => history.goBack()}>
        <FaArrowLeft /> {i18n.t('Common:back')}
      </Button>

      <Form style={{ clear: 'both' }}>
        <FormGroup>
          <Label for="accountName">{i18n.t('Common:name')}</Label>
          <Input
            type="text"
            name="name"
            id="accountName"
            placeholder={i18n.t('Common:name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </FormGroup>
        <FormGroup>
          <Label for="accountDescription">{i18n.t('Common:description')}</Label>
          <Input
            type="textarea"
            name="description"
            id="accountDescription"
            placeholder={i18n.t('Common:description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </FormGroup>
        <FormGroup>
          <Label for="accountEmail">{i18n.t('Common:email')}</Label>
          <Input
            type="email"
            name="email"
            id="accountEmail"
            placeholder={i18n.t('Common:email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </FormGroup>
        <FormGroup>
          <VytalCheckboxLabelContainer>
            <Checkbox
              checked={sendOrderFulfillmentEmails}
              onClick={() =>
                setSendOrderFulfillmentEmails(!sendOrderFulfillmentEmails)
              }
            />
            <CheckboxLabel
              onClick={() =>
                setSendOrderFulfillmentEmails(!sendOrderFulfillmentEmails)
              }
            >
              {i18n.t('EventAccountEditPage:sendHandoutReturnEmails')}
            </CheckboxLabel>
          </VytalCheckboxLabelContainer>
        </FormGroup>
        <FormGroup>
          <VytalCheckboxLabelContainer>
            <Checkbox
              checked={isBillable}
              onClick={() => setIsBillable(!isBillable)}
            />
            <CheckboxLabel onClick={() => setIsBillable(!isBillable)}>
              {i18n.t('EventAccountEditPage:isBillable')}
            </CheckboxLabel>
          </VytalCheckboxLabelContainer>
        </FormGroup>
        {errorUpdatingEventAcc && (
          <Alert color="danger">
            {i18n.t('Common:error')}: {errorUpdatingEventAcc.message}
          </Alert>
        )}
        {eventAccountData?.eventAccount?.external_zoho_id != null && (
          <Alert color="warning">
            {i18n.t('EventAccountEditPage:externalZohoWarning')}
          </Alert>
        )}
        <Button
          color="primary"
          onClick={handleUpdateEventAccount}
          disabled={
            updateLoading ||
            eventAccountData?.eventAccount?.external_zoho_id != null
          }
        >
          {i18n.t('EventAccountEditPage:updateEventAccount')}
        </Button>
      </Form>
    </div>
  );
};
